import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  CAutocomplete,
  CButton,
  CCallout,
  CCard,
  CCardBody,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CStepper,
  CSpinner,
} from "@coreui/react-pro";
import PublicAuthLayout from "../components/public/PublicAuthLayout";
import config from "../config/config";
import { buildAuthFetchOptions } from "../utils/authSession";
import "../styles/public-experience.css";

const FRONTDOOR_STORAGE_KEY = "e4e_applicant_frontdoor_key";
const DEFAULT_TARGET_MARKETS = [
  { code: "DE", label: "Deutschland", note: "Aktueller Fokus für Applicant Phase A" },
];

const benefits = [
  {
    label: "Frontdoor",
    title: "Erst verstehen, dann loslegen",
    text: "Der Einstieg ordnet zuerst Lage, Beruf, Sprache und Unterlagen ein, bevor der Applicant-Bereich startet.",
  },
  {
    label: "Produktiv",
    title: "Kein statisches Profilformular",
    text: "Der Quick Check führt in wenigen Minuten zu einem klaren Pfad mit konkreten nächsten Schritten.",
  },
  {
    label: "Anschluss",
    title: "Direkt weiter in den bestehenden Applicant-Bereich",
    text: "Die Ergebnisse bleiben speicherbar und leiten direkt in CV, Sprache, Unterlagen und Nachrichten weiter.",
  },
];

const scenarioCards = [
  {
    key: "abroad",
    eyebrow: "Applicant Frontdoor",
    title: "Ich bin Fachkraft im Ausland",
    description: "Ich möchte zuerst klären, ob ich bereits matchnah bin oder zuerst Sprache, Dokumente oder Anerkennung ordnen sollte.",
  },
  {
    key: "in_germany",
    eyebrow: "Applicant Frontdoor",
    title: "Ich bin bereits in Deutschland",
    description: "Ich bin schon vor Ort und möchte prüfen, wie direkt ich in einen Bewerbungs- oder Matching-Pfad weitergehen kann.",
  },
  {
    key: "unsure",
    eyebrow: "Applicant Frontdoor",
    title: "Ich bin unsicher und möchte zuerst prüfen",
    description: "Ich brauche zuerst eine neutrale Ersteinschätzung, welcher Weg für meine Situation überhaupt gilt.",
  },
];

const stepDefinitions = [
  { key: "starting_point", title: "1. Ausgangslage", description: "Wo Sie gerade stehen und von wo aus Sie nach Deutschland schauen." },
  { key: "profession", title: "2. Beruf und Ausbildung", description: "Zielberuf, Berufsfeld und Ausbildungsstand kurz einordnen." },
  { key: "documents", title: "3. Sprache und Unterlagen", description: "Deutschstand, Zertifikat und Dokumentenlage einordnen." },
  { key: "triage", title: "4. Anerkennung und Arbeitgeber", description: "Anerkennung, Arbeitgeberlage und Aufenthalt für den Pfad klären." },
  { key: "review", title: "5. Ergebnis", description: "Antworten prüfen und die Ersteinschätzung berechnen." },
];

const professionCategories = [
  { code: "healthcare", label: "Gesundheit und Pflege" },
  { code: "hospitality", label: "Hotel, Gastronomie und Service" },
  { code: "logistics", label: "Logistik, Lager und Transport" },
  { code: "skilled_trades", label: "Handwerk und technische Berufe" },
  { code: "industrial", label: "Industrie und Produktion" },
  { code: "office", label: "Büro, Verwaltung und kaufmännische Berufe" },
  { code: "it_digital", label: "IT und digitale Berufe" },
  { code: "other", label: "Anderes Berufsfeld" },
];
const regulatedOptions = [
  { value: "yes", label: "Ja, vermutlich reglementiert" },
  { value: "no", label: "Nein, eher nicht reglementiert" },
  { value: "unknown", label: "Unklar" },
];
const qualificationOptions = [
  { value: "completed", label: "Abschluss vorhanden" },
  { value: "in_training", label: "Noch in Ausbildung oder Abschluss läuft" },
  { value: "none", label: "Keine formale Ausbildung" },
  { value: "unknown", label: "Unklar" },
];
const careerGoalOptions = [
  { value: "qualified_employment", label: "Ich suche einen Job als Fachkraft oder Berufseinstieg" },
  { value: "training_or_apprenticeship", label: "Ich suche eine Ausbildung oder einen Ausbildungsplatz" },
  { value: "unclear", label: "Ich bin noch unsicher" },
];
const languageOptions = [
  { value: "none", label: "Kein Deutsch" },
  { value: "a1", label: "A1" },
  { value: "a2", label: "A2" },
  { value: "b1", label: "B1" },
  { value: "b2", label: "B2" },
  { value: "c1", label: "C1 oder höher" },
];
const languageCertificateOptions = [
  { value: "has_certificate", label: "Ja, mit Zertifikat" },
  { value: "no_certificate", label: "Noch ohne Zertifikat" },
  { value: "unknown", label: "Unklar" },
];
const documentsOptions = [
  { value: "complete", label: "Vollständig oder fast vollständig" },
  { value: "partial", label: "Teilweise vorhanden" },
  { value: "unclear", label: "Unklar oder unsortiert" },
];
const recognitionOptions = [
  { value: "not_started", label: "Noch nicht begonnen" },
  { value: "in_progress", label: "Läuft bereits" },
  { value: "completed", label: "Schon abgeschlossen" },
  { value: "unclear", label: "Unklar" },
];
const employerStatusOptions = [
  { value: "no_employer", label: "Noch kein Arbeitgeber" },
  { value: "contact_present", label: "Kontakt vorhanden" },
  { value: "offer_present", label: "Angebot vorhanden" },
];
const visaOptions = [
  { value: "unclear", label: "Noch unklar" },
  { value: "planned", label: "Geplant oder in Vorbereitung" },
  { value: "already_settled", label: "Schon geregelt" },
];

const initialFormState = {
  entry_context: "unsure",
  current_country_iso_code: "",
  nationality_iso_code: "",
  target_market: "DE",
  profession_label: "",
  profession_lookup_id: null,
  profession_category: "",
  regulated_profession_status: "",
  qualification_status: "",
  career_goal: "",
  experience_band: "unknown",
  german_level: "",
  language_certificate_status: "",
  documents_status: "",
  cv_available: null,
  recognition_status: "",
  employer_status: "",
  employer_contacts: null,
  work_readiness: "exploring",
  visa_status: "",
  currently_in_germany: null,
  support_needs: [],
};

async function fetchJson(path, options = {}) {
  const response = await fetch(
    `${config.API_BASE_URL}${path}`,
    buildAuthFetchOptions({
      ...options,
      headers: {
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
    })
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
}

function normalizeEntryParam(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["abroad", "in_germany", "unsure"].includes(normalized)) return normalized;
  return "unsure";
}

function setLocalFrontdoorKey(value) {
  if (value) localStorage.setItem(FRONTDOOR_STORAGE_KEY, value);
}

function getLocalFrontdoorKey() {
  return localStorage.getItem(FRONTDOOR_STORAGE_KEY) || "";
}

function findOptionLabel(options, value) {
  return options.find((item) => item.value === value)?.label || "Noch offen";
}

function normalizeCountryFallbackValue(value) {
  return String(value || "").trim().slice(0, 2).toUpperCase();
}

function normalizeProfessionSelection(option) {
  if (!option) return { label: "", lookupId: null };
  if (typeof option === "string") return { label: option, lookupId: null };
  const label = String(option.label || option.value || "").trim();
  const lookupId = option.value != null && option.value !== label ? option.value : null;
  return { label, lookupId };
}

function buildBootstrapFallback(error) {
  const status = Number(error?.status || 0);
  return {
    countries: [],
    target_markets: DEFAULT_TARGET_MARKETS,
    profession_categories: professionCategories,
    latest_intake: null,
    bootstrap_warning:
      status === 404
        ? "Die lokale Frontdoor-API war noch nicht verfügbar. Der Quick Check läuft vorübergehend mit einem CoreUI-konformen Fallback für Zielmarkt und Berufsfelder weiter."
        : "Der Quick Check nutzt vorübergehend einen CoreUI-konformen Fallback für Zielmarkt und Berufsfelder.",
  };
}

function isSafeApplicantTarget(href) {
  return /^\/dashboard\/applicant(?:[/?].*)?$/u.test(String(href || "").trim());
}

function buildRegisterHref(targetHref, externalKey) {
  const params = new URLSearchParams();
  params.set("role", "applicant");
  if (externalKey) params.set("frontdoor", externalKey);
  if (isSafeApplicantTarget(targetHref)) params.set("next", targetHref);
  return `/register?${params.toString()}`;
}

function buildLoginHref(targetHref, externalKey) {
  const params = new URLSearchParams();
  if (externalKey) params.set("frontdoor", externalKey);
  if (isSafeApplicantTarget(targetHref)) params.set("next", targetHref);
  const query = params.toString();
  return query ? `/login?${query}` : "/login";
}

function buildActionHref(targetHref, externalKey, isApplicantAuthenticated) {
  if (isApplicantAuthenticated && isSafeApplicantTarget(targetHref)) return targetHref;
  return buildRegisterHref(targetHref, externalKey);
}

function deriveEmployerStatus(savedIntake) {
  if (savedIntake?.employer_status) return savedIntake.employer_status;
  if (savedIntake?.employer_contacts === true) return "contact_present";
  if (savedIntake?.employer_contacts === false) return "no_employer";
  return "";
}

function validateStep(stepIndex, form) {
  if (stepIndex === 0) {
    if (!form.current_country_iso_code) return "Bitte geben Sie Ihr aktuelles Aufenthaltsland an.";
    if (form.currently_in_germany === null) return "Bitte geben Sie an, ob Sie aktuell in Deutschland sind.";
  if (!form.nationality_iso_code) return "Bitte wählen Sie Ihre Staatsangehörigkeit.";
  }
  if (stepIndex === 1) {
    if (!form.profession_label.trim()) return "Bitte nennen Sie Ihren Beruf oder Ihre Berufsbezeichnung.";
  if (!form.profession_category) return "Bitte wählen Sie ein Berufsfeld.";
    if (!form.regulated_profession_status) return "Bitte ordnen Sie ein, ob der Beruf reglementiert ist.";
  if (!form.qualification_status) return "Bitte geben Sie Ihren Ausbildungs- oder Abschlussstand an.";
    if (!form.career_goal) return "Bitte geben Sie an, ob Ihr Ziel ein Fachkraftjob oder eine Ausbildung ist.";
  }
  if (stepIndex === 2) {
    if (!form.german_level) return "Bitte geben Sie Ihren aktuellen Deutschstand an.";
    if (!form.language_certificate_status) return "Bitte geben Sie an, ob Sie bereits ein Sprachzertifikat haben.";
    if (!form.documents_status) return "Bitte ordnen Sie Ihre Dokumentenlage ein.";
    if (form.cv_available === null) return "Bitte geben Sie an, ob bereits ein Lebenslauf vorliegt.";
  }
  if (stepIndex === 3) {
    if (!form.recognition_status) return "Bitte ordnen Sie Ihren Anerkennungsstand ein.";
    if (!form.employer_status) return "Bitte geben Sie an, ob es bereits einen Arbeitgeber gibt.";
  if (!form.visa_status) return "Bitte geben Sie Ihren Visa- oder Aufenthaltsstatus an.";
  }
  return "";
}

function ReviewGrid({ rows }) {
  return (
    <div className="applicant-frontdoor-review-grid">
      {rows.map((row) => (
        <article key={row.label} className="applicant-frontdoor-review-card">
          <span>{row.label}</span>
          <strong>{row.value}</strong>
        </article>
      ))}
    </div>
  );
}

export default function ApplicantFrontdoorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const didHydrateSavedIntakeRef = useRef(false);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState("");
  const [bootstrapWarning, setBootstrapWarning] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [notice, setNotice] = useState("");
  const [countries, setCountries] = useState([]);
  const [targetMarkets, setTargetMarkets] = useState([]);
  const [dynamicProfessionCategories, setDynamicProfessionCategories] = useState([]);
  const [userContext, setUserContext] = useState(null);
  const [savedIntake, setSavedIntake] = useState(null);
  const [result, setResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const stepperLayout = "vertical";
  const [formState, setFormState] = useState(() => ({ ...initialFormState, entry_context: normalizeEntryParam(searchParams.get("entry")) }));
  const [professionSuggestions, setProfessionSuggestions] = useState([]);
  const [professionLoading, setProfessionLoading] = useState(false);
  const deferredProfessionQuery = useDeferredValue(formState.profession_label);
  const localFrontdoorKey = useMemo(() => getLocalFrontdoorKey(), []);

  const categories = dynamicProfessionCategories.length ? dynamicProfessionCategories : professionCategories;
  const countriesByCode = useMemo(() => Object.fromEntries(countries.map((country) => [country.iso_code, country.name])), [countries]);
  const applicantAuthenticated = userContext?.role === "applicant";
  const professionAutocompleteOptions = useMemo(() => {
    const mapped = professionSuggestions.map((item) => ({ value: item.id, label: item.beruf }));
    if (formState.profession_label && !mapped.some((item) => item.label === formState.profession_label)) {
      mapped.unshift({ value: formState.profession_label, label: formState.profession_label });
    }
    return mapped;
  }, [formState.profession_label, professionSuggestions]);
  const reviewRows = useMemo(() => [
    { label: "Ausgangspunkt", value: scenarioCards.find((item) => item.key === formState.entry_context)?.title || "Noch offen" },
    { label: "Aktuelles Aufenthaltsland", value: countriesByCode[formState.current_country_iso_code] || formState.current_country_iso_code || "Noch offen" },
    { label: "Bereits in Deutschland", value: formState.currently_in_germany === true ? "Ja" : formState.currently_in_germany === false ? "Nein" : "Noch offen" },
    { label: "Staatsangehörigkeit", value: countriesByCode[formState.nationality_iso_code] || formState.nationality_iso_code || "Noch offen" },
    { label: "Zielmarkt", value: targetMarkets.find((item) => item.code === formState.target_market)?.label || "Noch offen" },
    { label: "Beruf", value: formState.profession_label || "Noch offen" },
    { label: "Berufsfeld", value: categories.find((item) => item.code === formState.profession_category)?.label || "Noch offen" },
    { label: "Reglementierter Beruf", value: findOptionLabel(regulatedOptions, formState.regulated_profession_status) },
    { label: "Abschluss", value: findOptionLabel(qualificationOptions, formState.qualification_status) },
    { label: "Ziel in Deutschland", value: findOptionLabel(careerGoalOptions, formState.career_goal) },
    { label: "Deutsch", value: findOptionLabel(languageOptions, formState.german_level) },
    { label: "Sprachzertifikat", value: findOptionLabel(languageCertificateOptions, formState.language_certificate_status) },
    { label: "Dokumentenlage", value: findOptionLabel(documentsOptions, formState.documents_status) },
    { label: "Lebenslauf vorhanden", value: formState.cv_available === true ? "Ja" : formState.cv_available === false ? "Nein" : "Noch offen" },
    { label: "Anerkennungsstand", value: findOptionLabel(recognitionOptions, formState.recognition_status) },
    { label: "Arbeitgeber", value: findOptionLabel(employerStatusOptions, formState.employer_status) },
    { label: "Visa-/Aufenthaltsstatus", value: findOptionLabel(visaOptions, formState.visa_status) },
  ], [categories, countriesByCode, formState, targetMarkets]);
  const frontdoorStepperSteps = useMemo(
    () => stepDefinitions.map((step) => ({ label: step.title.replace(/^\d+\.\s*/, "").replace("Sprache und Unterlagen", "Sprache & Unterlagen") })),
    [],
  );

  useEffect(() => {
    document.title = "Experts4Europe | Applicant Quick Check";
  }, []);

  useEffect(() => {
    const entry = normalizeEntryParam(searchParams.get("entry"));
    setFormState((prev) => ({
      ...prev,
      entry_context: entry,
      currently_in_germany: entry === "in_germany" ? true : prev.currently_in_germany,
      current_country_iso_code: entry === "in_germany" ? "DE" : prev.current_country_iso_code,
    }));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const query = localFrontdoorKey ? `?external_key=${encodeURIComponent(localFrontdoorKey)}` : "";
        const data = await fetchJson(`/api/applicant/frontdoor/bootstrap${query}`);
        if (cancelled) return;
        setCountries(Array.isArray(data?.countries) ? data.countries : []);
        setTargetMarkets(Array.isArray(data?.target_markets) && data.target_markets.length ? data.target_markets : DEFAULT_TARGET_MARKETS);
        setDynamicProfessionCategories(Array.isArray(data?.profession_categories) ? data.profession_categories : []);
        setUserContext(data?.user_context || null);
        setSavedIntake(data?.latest_intake || null);
        setBootstrapWarning(String(data?.bootstrap_warning || "").trim());
      } catch (error) {
        if (cancelled) return;
        const fallback = buildBootstrapFallback(error);
        setCountries(fallback.countries);
        setTargetMarkets(fallback.target_markets);
        setDynamicProfessionCategories(fallback.profession_categories);
        setUserContext(null);
        setSavedIntake(fallback.latest_intake);
        setBootstrapWarning(fallback.bootstrap_warning);
        setBootstrapError("");
      } finally {
        if (!cancelled) setBootstrapLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [localFrontdoorKey]);

  useEffect(() => {
    if (!savedIntake || didHydrateSavedIntakeRef.current) return;

    didHydrateSavedIntakeRef.current = true;
    setFormState((prev) => ({
      ...prev,
      entry_context: savedIntake.entry_context || prev.entry_context,
      current_country_iso_code: savedIntake.current_country_iso_code || prev.current_country_iso_code,
      nationality_iso_code: savedIntake.nationality_iso_code || prev.nationality_iso_code,
      target_market: savedIntake.target_market || prev.target_market,
      profession_label: savedIntake.profession_label || prev.profession_label,
      profession_lookup_id: savedIntake.profession_lookup_id ?? prev.profession_lookup_id,
      profession_category: savedIntake.profession_category || prev.profession_category,
      regulated_profession_status: savedIntake.regulated_profession_status || prev.regulated_profession_status,
      qualification_status: savedIntake.qualification_status || prev.qualification_status,
      career_goal: savedIntake.career_goal || prev.career_goal,
      experience_band: savedIntake.experience_band || prev.experience_band,
      german_level: savedIntake.german_level || prev.german_level,
      language_certificate_status: savedIntake.language_certificate_status || prev.language_certificate_status,
      documents_status: savedIntake.documents_status || prev.documents_status,
      cv_available: savedIntake.cv_available ?? prev.cv_available,
      recognition_status: savedIntake.recognition_status || prev.recognition_status,
      employer_status: deriveEmployerStatus(savedIntake) || prev.employer_status,
      employer_contacts:
        savedIntake.employer_contacts ??
        (deriveEmployerStatus(savedIntake) === "offer_present" || deriveEmployerStatus(savedIntake) === "contact_present"
          ? true
          : deriveEmployerStatus(savedIntake) === "no_employer"
            ? false
            : prev.employer_contacts),
      work_readiness: savedIntake.work_readiness || prev.work_readiness,
      visa_status: savedIntake.visa_status || prev.visa_status,
      currently_in_germany: savedIntake.currently_in_germany ?? prev.currently_in_germany,
      support_needs: Array.isArray(savedIntake.support_needs) ? savedIntake.support_needs : prev.support_needs,
    }));

    if (savedIntake.result) {
      setResult(savedIntake.result);
      setCurrentStep(stepDefinitions.length - 1);
      setNotice("Zuletzt gespeicherte Ersteinschätzung geladen.");
    }
  }, [savedIntake]);

  useEffect(() => {
    const query = String(deferredProfessionQuery || "").trim();
    if (query.length < 2) {
      setProfessionSuggestions([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setProfessionLoading(true);
        const response = await fetch(`${config.API_BASE_URL}/api/stb/berufe?q=${encodeURIComponent(query)}&limit=8`, { signal: controller.signal });
        const data = await response.json().catch(() => []);
        if (!controller.signal.aborted) setProfessionSuggestions(Array.isArray(data) ? data : []);
      } catch (_err) {
        if (!controller.signal.aborted) setProfessionSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setProfessionLoading(false);
      }
    }, 220);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [deferredProfessionQuery]);

  const updateField = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setSubmitError("");
  };

  const applyScenario = (scenarioKey) => {
    setFormState((prev) => {
      if (scenarioKey === "in_germany") {
        return {
          ...prev,
          entry_context: "in_germany",
          currently_in_germany: true,
          current_country_iso_code: "DE",
        };
      }

      if (scenarioKey === "abroad") {
        return {
          ...prev,
          entry_context: "abroad",
          currently_in_germany: prev.currently_in_germany === true ? false : prev.currently_in_germany,
          current_country_iso_code: prev.current_country_iso_code === "DE" ? "" : prev.current_country_iso_code,
        };
      }

      return {
        ...prev,
        entry_context: "unsure",
        currently_in_germany: prev.currently_in_germany === true ? null : prev.currently_in_germany,
      };
    });
    setSubmitError("");
    setNotice("");
    setCurrentStep(0);
  };

  const goToStep = (stepNumber) => {
    const normalizedStep = Number(stepNumber);
    if (!Number.isFinite(normalizedStep)) return;
    const nextIndex = Math.max(0, Math.min(stepDefinitions.length - 1, normalizedStep - 1));

    if (nextIndex <= currentStep) {
      setSubmitError("");
      setCurrentStep(nextIndex);
      return;
    }

    for (let stepIndex = currentStep; stepIndex < nextIndex; stepIndex += 1) {
      const error = validateStep(stepIndex, formState);
      if (error) {
        setSubmitError(error);
        setCurrentStep(stepIndex);
        return;
      }
    }

    setSubmitError("");
    setCurrentStep(nextIndex);
  };

  const activeFrontdoorKey = savedIntake?.external_key || localFrontdoorKey;
  const routeActions = Array.isArray(result?.route_actions) ? result.route_actions : [];
  const primaryWorkspace = result?.next_workspace || routeActions[0] || null;
  const primaryTargetHref = primaryWorkspace?.target_href || primaryWorkspace?.href || "/dashboard/applicant";
  const primaryActionHref = buildActionHref(primaryTargetHref, activeFrontdoorKey, applicantAuthenticated);
  const loginActionHref = buildLoginHref(primaryTargetHref, activeFrontdoorKey);

  const rightPane = (
    <CCard className="public-core-auth-card public-core-auth-card--wide applicant-frontdoor-card">
      <CCardBody>
        <CStepper
          activeStepNumber={currentStep + 1}
          layout={stepperLayout}
          onStepChange={goToStep}
          steps={frontdoorStepperSteps}
        />

        {!result ? (
          <>
            <div className="public-core-auth-card__head">
              <span className="public-core-eyebrow">Applicant Frontdoor</span>
              <h2>Ersteinschätzung vor Registrierung, CV und Dokumentenpfad</h2>
              <p>Der Flow bleibt kompakt, speichert echte Intake-Daten und leitet Sie danach in den richtigen nächsten Arbeitsraum.</p>
            </div>

            <div className="applicant-frontdoor-scenario-grid">
              {scenarioCards.map((item) => (
                <CCard
                  key={item.key}
                  className={`public-core-role-card applicant-frontdoor-scenario-card ${
                    formState.entry_context === item.key ? "is-active" : ""
                  } ${item.key === "unsure" ? "applicant-frontdoor-scenario-card--full" : ""}`.trim()}
                  role="button"
                  tabIndex={0}
                  onClick={() => applyScenario(item.key)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      applyScenario(item.key);
                    }
                  }}
                >
                  <CCardBody>
                    <span className="public-core-role-card__eyebrow">{item.eyebrow}</span>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <span className="public-core-role-card__cta">Diesen Einstieg nutzen</span>
                  </CCardBody>
                </CCard>
              ))}
            </div>

            <div className="public-core-role-context applicant-frontdoor-context">
              <strong>{stepDefinitions[currentStep].title}</strong>
              <p>{stepDefinitions[currentStep].description}</p>
            </div>

            <div className="public-core-form">
              {bootstrapLoading ? <div className="applicant-frontdoor-inline-state"><CSpinner size="sm" /> Quick Check wird vorbereitet</div> : null}

              {currentStep === 0 ? (
                <div className="applicant-frontdoor-grid">
                  <div className="public-core-form-row"><CFormLabel>Aktuelles Aufenthaltsland</CFormLabel>{countries.length ? <CFormSelect className="public-core-input" value={formState.current_country_iso_code} onChange={(e) => updateField("current_country_iso_code", e.target.value)}><option value="">Bitte wählen</option>{countries.map((country) => <option key={country.iso_code} value={country.iso_code}>{country.name}</option>)}</CFormSelect> : <CFormInput className="public-core-input" maxLength={2} value={formState.current_country_iso_code} placeholder="z. B. PH" onChange={(e) => updateField("current_country_iso_code", normalizeCountryFallbackValue(e.target.value))} />}</div>
                  <div className="public-core-form-row"><CFormLabel>Staatsangehörigkeit</CFormLabel>{countries.length ? <CFormSelect className="public-core-input" value={formState.nationality_iso_code} onChange={(e) => updateField("nationality_iso_code", e.target.value)}><option value="">Bitte wählen</option>{countries.map((country) => <option key={country.iso_code} value={country.iso_code}>{country.name}</option>)}</CFormSelect> : <CFormInput className="public-core-input" maxLength={2} value={formState.nationality_iso_code} placeholder="z. B. BR" onChange={(e) => updateField("nationality_iso_code", normalizeCountryFallbackValue(e.target.value))} />}</div>
                  <div className="public-core-form-row"><CFormLabel>Zielmarkt</CFormLabel><CFormSelect className="public-core-input" value={formState.target_market} onChange={(e) => updateField("target_market", e.target.value)}><option value="">Bitte wählen</option>{targetMarkets.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row applicant-frontdoor-grid__full"><CFormLabel>Aktuell in Deutschland</CFormLabel><div className="applicant-frontdoor-choice-grid"><div className="public-core-checkbox"><CFormCheck type="radio" name="currently_in_germany" label="Ja" checked={formState.currently_in_germany === true} onChange={() => { updateField("currently_in_germany", true); updateField("current_country_iso_code", "DE"); }} /></div><div className="public-core-checkbox"><CFormCheck type="radio" name="currently_in_germany" label="Nein" checked={formState.currently_in_germany === false} onChange={() => updateField("currently_in_germany", false)} /></div></div></div>
                </div>
              ) : null}

              {currentStep === 1 ? (
                <div className="applicant-frontdoor-grid">
                  <div className="public-core-form-row applicant-frontdoor-grid__full">
                    <CFormLabel>Beruf oder Berufsbezeichnung</CFormLabel>
                    <CAutocomplete
                      className="public-core-input applicant-frontdoor-autocomplete"
                      allowOnlyDefinedOptions={false}
                      cleaner
                      highlightOptionsOnSearch
                      loading={professionLoading}
                      options={professionAutocompleteOptions}
                      placeholder="z. B. Pflegefachkraft, Koch, Elektriker oder Großhandelskaufmann"
                      search="external"
                      searchNoResultsLabel="Kein passender Beruf gefunden"
                      showHints
                      value={formState.profession_label}
                      onInput={(value) => setFormState((prev) => ({ ...prev, profession_label: value, profession_lookup_id: null }))}
                      onChange={(option) => {
                        const selection = normalizeProfessionSelection(option);
                        setFormState((prev) => ({
                          ...prev,
                          profession_label: selection.label,
                          profession_lookup_id: selection.lookupId,
                        }));
                      }}
                    />
                    {professionLoading ? <div className="applicant-frontdoor-inline-state"><CSpinner size="sm" /> Berufsvorschläge werden geladen</div> : null}
                  </div>
                  <div className="public-core-form-row"><CFormLabel>Berufsfeld</CFormLabel><CFormSelect className="public-core-input" value={formState.profession_category} onChange={(e) => updateField("profession_category", e.target.value)}><option value="">Bitte wählen</option>{categories.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row"><CFormLabel>Reglementierter Beruf</CFormLabel><CFormSelect className="public-core-input" value={formState.regulated_profession_status} onChange={(e) => updateField("regulated_profession_status", e.target.value)}><option value="">Bitte wählen</option>{regulatedOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row"><CFormLabel>Abschluss oder Ausbildungsstatus</CFormLabel><CFormSelect className="public-core-input" value={formState.qualification_status} onChange={(e) => updateField("qualification_status", e.target.value)}><option value="">Bitte wählen</option>{qualificationOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row applicant-frontdoor-grid__full"><CFormLabel>Was ist Ihr Ziel in Deutschland?</CFormLabel><CFormSelect className="public-core-input" value={formState.career_goal} onChange={(e) => updateField("career_goal", e.target.value)}><option value="">Bitte wählen</option>{careerGoalOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                </div>
              ) : null}

              {currentStep === 2 ? (
                <div className="applicant-frontdoor-grid">
                  <div className="public-core-form-row"><CFormLabel>Deutschstand</CFormLabel><CFormSelect className="public-core-input" value={formState.german_level} onChange={(e) => updateField("german_level", e.target.value)}><option value="">Bitte wählen</option>{languageOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row"><CFormLabel>Sprachzertifikat</CFormLabel><CFormSelect className="public-core-input" value={formState.language_certificate_status} onChange={(e) => updateField("language_certificate_status", e.target.value)}><option value="">Bitte wählen</option>{languageCertificateOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row"><CFormLabel>Dokumentenlage</CFormLabel><CFormSelect className="public-core-input" value={formState.documents_status} onChange={(e) => updateField("documents_status", e.target.value)}><option value="">Bitte wählen</option>{documentsOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row applicant-frontdoor-grid__full"><CFormLabel>Lebenslauf vorhanden</CFormLabel><div className="applicant-frontdoor-choice-grid"><div className="public-core-checkbox"><CFormCheck type="radio" name="cv_available" label="Ja" checked={formState.cv_available === true} onChange={() => updateField("cv_available", true)} /></div><div className="public-core-checkbox"><CFormCheck type="radio" name="cv_available" label="Nein" checked={formState.cv_available === false} onChange={() => updateField("cv_available", false)} /></div></div></div>
                </div>
              ) : null}

              {currentStep === 3 ? (
                <div className="applicant-frontdoor-grid">
                  <div className="public-core-form-row"><CFormLabel>Anerkennungsstand</CFormLabel><CFormSelect className="public-core-input" value={formState.recognition_status} onChange={(e) => updateField("recognition_status", e.target.value)}><option value="">Bitte wählen</option>{recognitionOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row"><CFormLabel>Arbeitgeberstatus</CFormLabel><CFormSelect className="public-core-input" value={formState.employer_status} onChange={(e) => { updateField("employer_status", e.target.value); updateField("employer_contacts", e.target.value === "offer_present" || e.target.value === "contact_present"); }}><option value="">Bitte wählen</option>{employerStatusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-form-row"><CFormLabel>Visa- oder Aufenthaltsstatus</CFormLabel><CFormSelect className="public-core-input" value={formState.visa_status} onChange={(e) => updateField("visa_status", e.target.value)}><option value="">Bitte wählen</option>{visaOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</CFormSelect></div>
                  <div className="public-core-role-context applicant-frontdoor-context applicant-frontdoor-grid__full">
                    <strong>Nur der nächste sinnvolle Schritt</strong>
                    <p>Die Frontdoor entscheidet hier noch nichts endgültig. Sie ordnet nur, ob Sprache, Unterlagen, Anerkennung oder direkter Applicant-Start gerade zuerst sinnvoll ist.</p>
                  </div>
                </div>
              ) : null}

              {currentStep === 4 ? <ReviewGrid rows={reviewRows} /> : null}

              {bootstrapWarning ? <CCallout color="warning" className="public-core-alert applicant-frontdoor-callout">{bootstrapWarning}</CCallout> : null}
              {bootstrapError ? <CCallout color="warning" className="public-core-alert applicant-frontdoor-callout">{bootstrapError}</CCallout> : null}
              {submitError ? <CCallout color="danger" className="public-core-alert applicant-frontdoor-callout">{submitError}</CCallout> : null}
              {notice ? <CCallout color="success" className="public-core-alert applicant-frontdoor-callout">{notice}</CCallout> : null}

              <div className="public-core-form-actions">
                <div className="applicant-frontdoor-action-row">
                  <CButton
                    type="button"
                    className="public-core-btn public-core-btn--light"
                    disabled={currentStep === 0 || submitting || bootstrapLoading}
                    onClick={() => goToStep(currentStep)}
                  >
                    Zurück
                  </CButton>
                  {currentStep < stepDefinitions.length - 1 ? (
                    <CButton
                      type="button"
                      className="public-core-btn public-core-btn--primary"
                      disabled={submitting || bootstrapLoading}
                      onClick={() => goToStep(currentStep + 2)}
                    >
                      Weiter
                    </CButton>
                  ) : (
                    <CLoadingButton type="button" className="public-core-btn public-core-btn--primary" disabledOnLoading disabled={bootstrapLoading} loading={submitting} onClick={async () => {
                      const error = validateStep(currentStep, formState);
                      if (error) return setSubmitError(error);
                      try {
                        setSubmitting(true);
                        const payload = {
                          ...formState,
                          employer_contacts:
                            formState.employer_status === "offer_present" || formState.employer_status === "contact_present"
                              ? true
                              : formState.employer_status === "no_employer"
                                ? false
                                : formState.employer_contacts,
                          current_country_label: countriesByCode[formState.current_country_iso_code] || formState.current_country_iso_code || "",
                          nationality_label: countriesByCode[formState.nationality_iso_code] || formState.nationality_iso_code || "",
                          source_page: location.pathname,
                        };
                        const data = await fetchJson("/api/applicant/frontdoor/intakes", { method: "POST", body: JSON.stringify(payload) });
                        if (data?.intake?.external_key) setLocalFrontdoorKey(data.intake.external_key);
                        setSavedIntake(data?.intake || null);
                        setResult(data?.result || data?.intake?.result || null);
                        setNotice("Ihre Ersteinschätzung wurde gespeichert.");
                        setSubmitError("");
                      } catch (error) {
                        setSubmitError(error.message || "Die Ersteinschätzung konnte nicht gespeichert werden.");
                      } finally {
                        setSubmitting(false);
                      }
                    }}>{submitting ? "Berechne Ergebnis..." : "Ersteinschätzung berechnen"}</CLoadingButton>
                  )}
                </div>
              </div>
            </div>

            <div className="public-core-card-note">
              Noch unsicher? Sie können den Quick Check jederzeit erneut durchlaufen.
              <br />
              Bereits registriert? <Link to="/login">Anmelden</Link>
              <br />
              Direkt ohne Quick Check? <Link to="/register?role=applicant">Applicant-Zugang anlegen</Link>
            </div>
          </>
        ) : (
          <>
            <div className="public-core-auth-card__head">
              <span className="public-core-eyebrow">Applicant Ergebnis</span>
              <h2>{result.status_title || result.path_label}</h2>
              <p>{result.result_summary}</p>
            </div>

            <CCallout color={result.can_continue_now ? "success" : "warning"} className="public-core-role-context applicant-frontdoor-result-meta applicant-frontdoor-callout">
              <strong>Readiness {result.readiness_score} · {result.readiness_label}</strong>
              <p>{result.can_continue_now ? "Sie können jetzt direkt in den Applicant-Bereich weitergehen." : "Vor dem Matching sollte zuerst noch mindestens ein Kernblock geordnet werden."}</p>
              {Array.isArray(result.blocker_labels) && result.blocker_labels.length ? <span className="applicant-frontdoor-result-badges">Offen: {result.blocker_labels.join(" · ")}</span> : null}
            </CCallout>

            <CCallout color="info" className="public-core-role-context applicant-frontdoor-result-meta applicant-frontdoor-callout">
              <strong>Berufspfad · {result.profession_path_label || "Noch unklar"}</strong>
              <p>{result.profession_path_summary || "Der Quick Check ordnet Ihren Berufspfad zuerst fachlich ein, bevor er in den Applicant-Bereich weiterleitet."}</p>
            </CCallout>

            <div className="applicant-frontdoor-result-columns">
              <CCallout color="info" className="applicant-frontdoor-result-card applicant-frontdoor-callout">
                <strong>Warum dieser Pfad?</strong>
                <ul>{Array.isArray(result.reasoning) ? result.reasoning.map((item) => <li key={item}>{item}</li>) : null}</ul>
              </CCallout>
              <CCallout color={result.can_continue_now ? "success" : "warning"} className="applicant-frontdoor-result-card applicant-frontdoor-callout">
                <strong>Nächste Schritte</strong>
                <ol>{Array.isArray(result.next_steps) ? result.next_steps.map((item) => <li key={item.key}><strong>{item.label}</strong><span>{item.detail}</span></li>) : null}</ol>
              </CCallout>
            </div>

            {routeActions.length ? (
              <div className="applicant-frontdoor-route-grid">
                {routeActions.map((action) => {
                  const actionHref = buildActionHref(action.target_href || action.href, activeFrontdoorKey, applicantAuthenticated);
                  return (
                    <article key={action.key} className="applicant-frontdoor-route-card">
                      <span>{action.label}</span>
                      <strong>{action.detail}</strong>
                      <CButton type="button" className="public-core-btn public-core-btn--light" onClick={() => navigate(actionHref)}>
                        {applicantAuthenticated ? "Jetzt öffnen" : "Konto anlegen und öffnen"}
                      </CButton>
                    </article>
                  );
                })}
              </div>
            ) : null}

            {!result.can_continue_now ? (
              <CCallout color="warning" className="applicant-frontdoor-callout">
                <strong>Guardrail vor dem nächsten Workspace</strong>
                <p>{result.profession_path_type === "training_path" ? "Dieser Weg führt zuerst über Ausbildung statt über direktes Fachkraft-Matching. Bitte ordnen Sie deshalb zuerst den passenden Einstiegspfad." : result.profession_path_type === "regulated_profession" ? "Dieser reglementierte Beruf braucht zuerst genug Klarheit zu Anerkennung, Sprache und Unterlagen, bevor ein belastbarer Matching-Start sinnvoll wird." : "Dieser Pfad ist noch nicht matchfähig. Bitte schließen Sie zuerst Sprache, Dokumente oder Anerkennung soweit, bis der Quick Check keinen Kernblocker mehr meldet."}</p>
              </CCallout>
            ) : null}

            <div className="public-core-form-actions">
              {primaryActionHref ? <CButton type="button" className="public-core-btn public-core-btn--primary" onClick={() => navigate(primaryActionHref)}>{applicantAuthenticated ? (primaryWorkspace?.label || "Weiter") : "Applicant-Zugang anlegen und weitergehen"}</CButton> : null}
              <CButton type="button" className="public-core-btn public-core-btn--light" onClick={() => { setResult(null); setNotice(""); setCurrentStep(stepDefinitions.length - 1); }}>Angaben anpassen</CButton>
            </div>

            {!applicantAuthenticated ? (
              <div className="public-core-card-note">
                Schon registriert? <Link to={loginActionHref}>Anmelden und direkt fortsetzen</Link>
              </div>
            ) : null}

            {savedIntake?.result ? (
              <div className="public-core-card-note">
                Ergebnis gespeichert. Externer Bezug: <code>{savedIntake.external_key}</code>
              </div>
            ) : null}
          </>
        )}
      </CCardBody>
    </CCard>
  );

  return (
    <PublicAuthLayout
      activeAuthPath={null}
      headerMode="marketing"
      shellPreset="standard"
      pageClassName="applicant-frontdoor-page"
      benefitColumnProps={{ xs: 12, md: 6, xl: 4 }}
      eyebrow="Experts4Europe Applicant Frontdoor"
      title="Quick Check vor CV, Sprache und Unterlagen"
      description="Fachkräfte ordnen Aufenthaltsort, Beruf, Sprache, Unterlagen, Anerkennung und Arbeitgeberlage in wenigen Schritten ein und erhalten danach einen klaren Applicant-Pfad mit echter Persistenz."
      benefits={benefits}
      rightPane={rightPane}
    />
  );
}
